/**
 * L'ONGLET INCARNE L'IDENTITÉ DE SA SESSION APPLICATIVE — la règle, en un seul
 * endroit, pour toutes les applications du parc.
 *
 * ─── Le défaut qu'elle ferme ────────────────────────────────────────────────
 *
 * Après une entrée, chaque application choisissait elle-même le compte de son
 * onglet, chacune à sa façon : le kiosk et Sign prenaient « le compte par
 * défaut » du trousseau, le backoffice cherchait par adresse puis retombait
 * sur le défaut. Or le défaut suit la DERNIÈRE connexion faite à la porte : en
 * ajoutant le compte de Mendel depuis la console de Yossi, Mendel devenait le
 * défaut, et l'onglet de Yossi l'adoptait — widget au nom de Mendel sur la
 * console de Yossi (production, 2026-11-04). C'est exactement le défaut que la
 * doctrine interdit « sous aucune forme » (fonctionnement-navigateur-trousseau,
 * §2) : un widget sur B face à une session signée par A.
 *
 * ─── La règle ───────────────────────────────────────────────────────────────
 *
 * L'application SAIT pour quelle identité sa session est ouverte : la
 * consommation d'un lancement la rend (`identite.id`, la clé de raccordement),
 * et le slot que l'on vient de choisir la porte (`identityId`). L'onglet adopte
 * le slot de CETTE identité — jamais le défaut, jamais une adresse. Le
 * trousseau tient au plus UNE session par identité et par tenant (§5) : la
 * désignation est donc sans ambiguïté.
 *
 * Ces deux fonctions n'utilisent que l'interface publique du runtime :
 * `useInThisTab` n'écrit RIEN côté serveur, aucun secret n'est touché.
 */
/**
 * Aligne le slot de CET onglet sur l'identité de la session applicative.
 *
 * Un slot vivant est préféré ; à défaut, la pierre tombale de la MÊME identité
 * (« Expirée ») — le widget proposera alors de se reconnecter à la bonne
 * personne, au lieu d'afficher quelqu'un d'autre. Aucun slot de l'identité :
 * on ne choisit RIEN (`'absent'`) ; `compteDeLaSession` refusera alors tout
 * autre slot.
 *
 * DÉGRADÉ, JAMAIS BLOQUANT : ne lève pas. Une entrée ne dépend pas du
 * trousseau ; l'appelant journalise le résultat s'il le souhaite.
 */
export async function alignerOngletSurIdentite(runtime, identite) {
    if (typeof identite !== 'string' || identite === '')
        return 'indisponible';
    try {
        const sessions = await runtime.listBrowserSessions();
        if (runtime.currentSession()?.identityId === identite)
            return 'deja-aligne';
        const siennes = sessions.filter((s) => s.identityId === identite);
        const cible = siennes.find((s) => s.state === 'live') ?? siennes.find((s) => s.state === 'expired');
        if (cible === undefined)
            return 'absent';
        await runtime.useInThisTab(cible.slotRef);
        return 'aligne';
    }
    catch {
        return 'indisponible';
    }
}
/**
 * Le compte de l'onglet, SEULEMENT s'il est celui de la session applicative.
 *
 * C'est le verrou du fournisseur de porteur du widget : un slot d'une AUTRE
 * identité ne reçoit aucun porteur — le widget se masque plutôt que d'afficher
 * quelqu'un d'autre que la personne connectée. Identité inconnue (session
 * ouverte avant que l'application ne la retienne) : aucun contrôle possible,
 * le compte de l'onglet est rendu tel quel.
 *
 * Synchrone, comme `currentSession()` : la liste doit avoir été relue au moins
 * une fois (après un rechargement, l'appelant relit d'abord).
 */
export function compteDeLaSession(runtime, identite) {
    const courant = runtime.currentSession();
    if (courant === null)
        return null;
    if (typeof identite !== 'string' || identite === '')
        return courant;
    return courant.identityId === identite ? courant : null;
}
//# sourceMappingURL=alignement.js.map